<template>
  <Card id="tasks-overview-card" class="gap-0">
    <CardHeader class="flex flex-wrap items-center justify-between gap-3 pb-4">
      <div class="flex items-center gap-4">
        <CardTitle>Tasks</CardTitle>
        <Filter :filters="filters" @filter-change="({ key, value }) => ((filters[key] as string) = value)" />
      </div>
      <div class="flex items-center gap-2">
        <div v-if="tasksQuery.isLoading.value" class="spinner"></div>
        <Button variant="card-header-action" size="sm" @click="showAssignTasks = true">Assign Checklist</Button>
      </div>
    </CardHeader>
    <CardContent>
      <p v-if="tasksQuery.errors.value" class="mb-2 text-sm text-red-600">
        Failed to load tasks: {{ tasksQuery.errors.value.join(', ') }}
      </p>
      <TaskList
        :tasks="filteredTasks"
        :is-loading="tasksQuery.isLoading.value"
        :contact-id="contactId"
        @task-deleted="tasksQuery.reload"
        @task-completed="tasksQuery.reload"
      />
      <OneOffTaskInput :controller="taskInput" action-title="Add" @action="handleAddTask" />
    </CardContent>

    <ContactAssignTasks
      v-if="showAssignTasks"
      :record="{ id: +contactId }"
      @handle-submit="handleTasksAssigned"
      @handle-cancel="showAssignTasks = false"
    />
  </Card>
</template>

<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { computed, reactive, ref } from 'vue';
import { OneOffTaskInput, useOneOffTaskInput } from '@/components/shared/OneOffTaskInput';
import Filter from './Filter.vue';
import TaskList from './TaskList.vue';
import ContactAssignTasks from '@/components/contact/ContactAssignTasks.vue';
import { useOverviewTaskParser, type OverviewTaskItem } from './useOverviewTaskParser';
import { useOverviewTaskFilter } from './useOverviewTaskFilter';
import type { GetContactTaskResponse } from '@/api/contacts';
import type { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import { defaultFilters, type Filters } from './filterConfiguration';

type TaskOverviewCardProps = {
  contactId: string;
  tasksQuery: ReturnType<typeof useHFetchQuery<GetContactTaskResponse[]>>;
};

const props = defineProps<TaskOverviewCardProps>();
const { parseContactTasks } = useOverviewTaskParser();
const { filterTasks } = useOverviewTaskFilter();

const tasksQuery = props.tasksQuery;

const taskInput = useOneOffTaskInput();
const filters = reactive<Filters>({ ...defaultFilters });
const showAssignTasks = ref(false);

const handleTasksAssigned = () => {
  tasksQuery.reload();
  showAssignTasks.value = false;
};

const sortedTasks = computed(() => {
  const parsedContactTasks = parseContactTasks(tasksQuery.data.value ?? []);
  return parsedContactTasks.sort(sortByNewestDueDate);
});

const sortByNewestDueDate = (a: OverviewTaskItem, b: OverviewTaskItem) => {
  const futureDate = new Date(5000, 1, 1);
  const aDate = a.dueDate ?? futureDate;
  const bDate = b.dueDate ?? futureDate;
  return aDate.getTime() - bDate.getTime();
};

const filteredTasks = computed(() =>
  filterTasks({
    assignee: filters.assignee,
    taskType: filters.taskType,
    status: filters.status,
    overviewTaskItems: sortedTasks.value,
  })
);

const handleAddTask = async () => {
  if (!taskInput.validate()) return;
  const success = await taskInput.submit(+props.contactId);
  if (success) tasksQuery.reload();
};
</script>
